import React, { memo, useState, useEffect } from "react";
import dayjs from "dayjs";
import { Alert, Stack, Icon, DateCalendar, DayCalendarSkeleton, Typography } from "investira.react.components";
import { objects, validators } from "investira.sdk";
import withResponseHandling from "../../hoc/withResponseHandling";
import ServerDay from "./ServerDay";

const formatarDatas = (pPosicoes) => {
  const datasAgrupadas = {};

  pPosicoes.forEach((xPosicao) => {
    const xData = dayjs(xPosicao.data);
    const xChaveAnoMes = xData.format("YYYY-MM");

    if (!datasAgrupadas[xChaveAnoMes]) {
      datasAgrupadas[xChaveAnoMes] = {
        year: xData.format("YYYY"),
        month: xData.format("MM"),
        highlightedDays: [],
        posicoesAbertas: {},
        custodianteConferencia: {}
      };
    }

    const dia = parseInt(xData.format("DD"));
    if (!datasAgrupadas[xChaveAnoMes].highlightedDays.includes(dia)) {
      datasAgrupadas[xChaveAnoMes].highlightedDays.push(dia);
      const xDataKey = xData.format("DDMMYYYY");
      datasAgrupadas[xChaveAnoMes].posicoesAbertas[xDataKey] = xPosicao.aberta;
      datasAgrupadas[xChaveAnoMes].custodianteConferencia[xDataKey] = xPosicao.custodiante_conferencia;
    }
  });

  return datasAgrupadas;
};

const formatarAnoMes = (pData) => {
  return pData.format("YYYY-MM");
};

const CalendarPosicoes = memo((props) => {
  const {
    carteiraId,
    dataPosicao,
    onlyHighlightAble = false,
    onChange,
    onStart,
    open,
    refreshHighlightedDays = false,
    datasPosicoes,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState([]);
  const [highlightedDays, setHighlightedDays] = useState([]);
  const [posicoesAbertas, setPosicoesAbertas] = useState([]);
  const [custodianteConferencia, setCustodianteConferencia] = useState([]);
  const [initialValue, setInitialValue] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleMonthChange = (pDate, pDays) => {
    const xKey = formatarAnoMes(pDate);
    const xHighlightedDays = pDays[xKey] ? pDays[xKey].highlightedDays : [];
    setHighlightedDays(xHighlightedDays);
    // Adiciona as posições abertas ao estado ao trocar de mês
    const xPosicoesAbertas = pDays[xKey] ? pDays[xKey].posicoesAbertas : [];
    setPosicoesAbertas(xPosicoesAbertas);
    const xCustodianteConferencia = pDays[xKey] ? pDays[xKey].custodianteConferencia : [];
    setCustodianteConferencia(xCustodianteConferencia);
  };

  const handleChange = (pDate, pDays) => {
    const xKey = formatarAnoMes(pDate);
    const xDay = pDate.date();
    const xHighlightedDays = pDays[xKey] ? pDays[xKey].highlightedDays : [];
    const isHighlighted = xHighlightedDays.includes(xDay);
    const xConfirmTipo = isHighlighted ? "S" : "N";
    const xPayload = {
      data: pDate,
      tipo: xConfirmTipo,
    };

    onChange && onChange(xPayload);
    setSelectedDate(pDate);
  };

  useEffect(() => {
    if (
      (open, !validators.isNull(dataPosicao) && validators.isNull(initialValue))
    ) {
      setInitialValue(dayjs(dataPosicao));
      setIsLoading(false);
    }
  }, [dataPosicao]);

  useEffect(() => {
    if (
      (open,
      !validators.isEmpty(carteiraId) && !validators.isEmpty(datasPosicoes))
    ) {
      const xDays = formatarDatas(objects.deepCopy(datasPosicoes));
      setDays(xDays);
      if (
        !validators.isEmpty(datasPosicoes) &&
        validators.isEmpty(initialValue)
      ) {
        onStart && onStart(dayjs(datasPosicoes[0].data));
        refreshHighlightedDays && handleChange(dayjs(props.dataPosicao), xDays);
        setSelectedDate(dayjs(props.dataPosicao))
      }
    }
  }, [carteiraId, initialValue, datasPosicoes, open]);

  useEffect(() => {
    if (
      (open, !validators.isEmpty(days) && !validators.isEmpty(initialValue))
    ) {
      handleMonthChange(initialValue, days);
    }
  }, [initialValue, days]);

  if (validators.isNull(initialValue)) {
    return null;
  }

  const xDay = !validators.isNull(selectedDate) && selectedDate.format("DDMMYYYY");
  const xHasCustodiantePosicao = custodianteConferencia[xDay] ?? false;

  return (
    <>
    {xHasCustodiantePosicao && <Stack gap={1} direction="row"><Icon iconName="information" color="primary" size={18}/><Typography variant="caption" color="textSecondary">Conferência custodiante disponível</Typography></Stack>}
    <DateCalendar
      sx={{
        ".MuiDayCalendar-monthContainer": {
          overflow: "visible",
        },
      }}
      defaultValue={initialValue}
      loading={isLoading}
      disabled={isLoading}
      onMonthChange={(pDate) => handleMonthChange(pDate, days)}
      onChange={(pDate) => handleChange(pDate, days)}
      renderLoading={() => <DayCalendarSkeleton />}
      slots={{
        day: ServerDay,
      }}
      slotProps={{
        day: {
          highlightedDays,
          posicoesAbertas,
          custodianteConferencia,
          onlyHighlightAble: onlyHighlightAble,
        },
      }}
    />
    </>
  );
});

export default withResponseHandling(CalendarPosicoes);
